import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    InlineStack,
    Page,
    Spinner,
    Text,
} from '@shopify/polaris';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import { AtdShipToHomeOrderLinkTarget } from '../../app/models/Atd';

const getErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (data && typeof data === 'object') return JSON.stringify(data);
    return error?.message || fallback;
};

const lookupRetryDelayMs = 2000;
// First lookup happens immediately, then 30 retries at 2 seconds each. Shopify's
// Request fulfillment callback can take close to a minute to create the ATD row.
const maxLookupAttempts = 31;

const waitForRetry = (delayMs: number) =>
    new Promise<void>((resolve) => {
        window.setTimeout(resolve, delayMs);
    });

const AtdOrderLinkRedirectPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [lookupAttempt, setLookupAttempt] = useState(1);
    const [lookupVersion, setLookupVersion] = useState(0);
    const [target, setTarget] = useState<AtdShipToHomeOrderLinkTarget | undefined>();
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        let cancelled = false;

        const resolveLinkTarget = async () => {
            try {
                setLoading(true);
                setError(undefined);
                setTarget(undefined);

                for (let attempt = 1; attempt <= maxLookupAttempts; attempt++) {
                    if (cancelled) return;

                    setLookupAttempt(attempt);

                    // Shopify sends us query params such as order_gid. The API owns
                    // matching those Shopify identifiers to our ATD order table.
                    const result = await agent.Atd.shipToHomeOrderLinkTarget(location.search);
                    if (cancelled) return;

                    if (result.found && result.atdShipToHomeOrderId) {
                        // Replace the transient link route so browser Back does not
                        // return merchants to this resolver page.
                        navigate(`/integrations/atd/orders/${result.atdShipToHomeOrderId}`, { replace: true });
                        return;
                    }

                    setTarget(result);

                    if (attempt < maxLookupAttempts) {
                        // Request fulfillment is handled by a durable background
                        // worker. A new order can land here before that worker has
                        // created the ATD preview row, so keep checking briefly.
                        await waitForRetry(lookupRetryDelayMs);
                    }
                }

                setTarget((current) => {
                    const didOnlyMissExistingOrder =
                        !current?.message ||
                        current.message === 'No ATD order was found for this Shopify order.';

                    return {
                        found: false,
                        shopifyOrderId: current?.shopifyOrderId,
                        shopifyOrderGid: current?.shopifyOrderGid,
                        shopifyOrderName: current?.shopifyOrderName,
                        message: didOnlyMissExistingOrder
                            ? 'No ATD order was found yet. Request fulfillment to the ATD app location first, then check again.'
                            : current.message
                    };
                });
            } catch (resolveError: any) {
                if (!cancelled) {
                    setError(getErrorMessage(resolveError, 'Unable to resolve this Shopify order to an ATD order.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        resolveLinkTarget();

        return () => {
            cancelled = true;
        };
    }, [location.search, lookupVersion, navigate]);

    return (
        <Page
            title="ATD order details"
            backAction={{ content: 'ATD orders', onAction: () => navigate('/integrations/atd/orders') }}
        >
            <Card>
                <Box padding="400">
                    {loading ? (
                        <InlineStack gap="300" blockAlign="center">
                            <Spinner accessibilityLabel="Finding ATD order" size="small" />
                            <Text as="p">
                                {lookupAttempt === 1
                                    ? 'Finding the matching ATD order...'
                                    : 'Waiting for the ATD order preview to be created...'}
                            </Text>
                        </InlineStack>
                    ) : (
                        <BlockStack gap="300">
                            {error ? (
                                <Banner tone="critical" title="Unable to open ATD order">
                                    <p>{error}</p>
                                </Banner>
                            ) : (
                                <Banner tone="warning" title="No ATD order found">
                                    <p>{target?.message || 'No ATD order was found for this Shopify order.'}</p>
                                </Banner>
                            )}
                            <InlineStack gap="200">
                                <Button onClick={() => setLookupVersion((value) => value + 1)}>Check again</Button>
                                <Button onClick={() => navigate('/integrations/atd/orders')}>Open ATD orders</Button>
                            </InlineStack>
                        </BlockStack>
                    )}
                </Box>
            </Card>
        </Page>
    );
};

export default AtdOrderLinkRedirectPage;
